import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const PEOPLE_BASE = '/people';
const PEOPLE_FILTERS = `${PEOPLE_BASE}/filters`;

// ── Email Check ─────────────────────────────────────────────────────────────

/**
 * useCheckEmailExists - check if email already exists across all customer types
 * @returns { checkEmail: (email, excludeCode?) => Promise<{ exists, matches }>, loading }
 */
export function useCheckEmailExists() {
  const [loading, setLoading] = useState(false);

  const checkEmail = useCallback(async (email, excludeCode) => {
    if (!email || !email.trim()) return { exists: false, matches: [] };
    setLoading(true);
    try {
      const params = { email: email.trim().toLowerCase() };
      if (excludeCode) params.excludeCode = excludeCode;
      const { data: res } = await axiosInstance.get(`${PEOPLE_BASE}/check-email`, { params });
      return res?.data ?? { exists: false, matches: [] };
    } catch {
      return { exists: false, matches: [] };
    } finally {
      setLoading(false);
    }
  }, []);

  return { checkEmail, loading };
}

// ── Private Customers ───────────────────────────────────────────────────────

/**
 * usePrivateCustomersList - fetch private customers with filters
 * @param {Object} params - { q, status, balance, page, limit, sort }
 * @returns { data, meta, pagination, loading, error, refetch }
 */
export function usePrivateCustomersList(params = {}) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const limit = params.limit ?? 10;
      const page = params.page ?? 1;
      const offset = (page - 1) * limit;

      const queryParams = {};
      if (params.q?.trim()) queryParams.q = params.q.trim();
      if (params.status && params.status !== 'All') {
        queryParams.status = params.status.toUpperCase();
      }
      if (params.balance && params.balance !== 'all') {
        queryParams.balance = params.balance === 'owed' ? 'Open Balance' : 'Fully Paid';
      }
      queryParams.limit = limit;
      queryParams.offset = offset;
      if (params.sort) queryParams.sort = params.sort;

      const { data: res } = await axiosInstance.get(PEOPLE_BASE, { params: queryParams });
      const list = res?.data ?? [];
      const m = res?.meta ?? null;
      setData(list);
      setMeta(m);

      const pagination = m
        ? { page: m.page ?? page, totalPages: m.pages ?? 1, total: m.total ?? list.length }
        : { page, totalPages: 1, total: list.length };
      return { ...res, pagination };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load customers';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.status, params.balance, params.page, params.limit, params.sort]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const meta_ = meta;
  const pagination = meta_
    ? { page: meta_.page ?? 1, totalPages: meta_.pages ?? 1, total: meta_.total ?? 0 }
    : { page: 1, totalPages: 1, total: 0 };

  return { data, meta, pagination, loading, error, refetch: fetchList };
}

/**
 * usePrivateCustomerById - fetch single private customer by customerCode
 */
export function usePrivateCustomerById(customerCode) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!customerCode) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${PEOPLE_BASE}/${customerCode}`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load customer';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [customerCode]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

/**
 * useCreatePrivateCustomer - create private customer
 */
export function useCreatePrivateCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        hasEmail: payload.hasEmail ?? true,
        email: payload.hasEmail === false ? null : payload.email,
        phone: payload.phone,
        nationality: payload.nationality,
        preferredLanguage: payload.language,
        dateOfBirth: payload.dateOfBirth || undefined,
        gender: payload.gender ? payload.gender.toUpperCase().replace(/\s+/g, '_') : undefined,
        street: payload.address?.street,
        houseNumber: payload.address?.houseNumber || undefined,
        boxNumber: payload.address?.boxNumber || undefined,
        city: payload.address?.city,
        postalCode: payload.address?.zip,
        country: payload.address?.country,
      };
      const { data: res } = await axiosInstance.post(PEOPLE_BASE, apiPayload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create customer';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useUpdatePrivateCustomer - update private customer
 */
export function useUpdatePrivateCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (customerCode, payload) => {
    if (!customerCode) throw new Error('Customer code is required');
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        firstName: payload.firstName,
        lastName: payload.lastName,
        hasEmail: payload.hasEmail ?? true,
        email: payload.hasEmail === false ? null : payload.email,
        phone: payload.phone,
        nationality: payload.nationality,
        preferredLanguage: payload.language,
        dateOfBirth: payload.dateOfBirth || undefined,
        gender: payload.gender ? payload.gender.toUpperCase().replace(/\s+/g, '_') : undefined,
        street: payload.address?.street,
        houseNumber: payload.address?.houseNumber || undefined,
        boxNumber: payload.address?.boxNumber || undefined,
        city: payload.address?.city,
        postalCode: payload.address?.zip,
        country: payload.address?.country,
      };
      const { data: res } = await axiosInstance.put(`${PEOPLE_BASE}/${customerCode}`, apiPayload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update customer';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

// ── Professional Customers ─────────────────────────────────────────────────

const PRO_BASE = `${PEOPLE_BASE}/professional-customers`;

export function useProfessionalCustomersList(params = {}) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const limit = params.limit ?? 10;
      const page = params.page ?? 1;
      const offset = (page - 1) * limit;

      const queryParams = {};
      if (params.q?.trim()) queryParams.q = params.q.trim();
      if (params.status && params.status !== 'All') queryParams.status = params.status.toUpperCase();
      if (params.balance && params.balance !== 'all') {
        queryParams.balance = params.balance === 'owed' ? 'Open Balance' : 'Fully Paid';
      }
      if (params.industry) queryParams.industry = params.industry;
      queryParams.limit = limit;
      queryParams.offset = offset;
      if (params.sort) queryParams.sort = params.sort;

      const { data: res } = await axiosInstance.get(PRO_BASE, { params: queryParams });
      const list = res?.data ?? [];
      const m = res?.meta ?? null;
      setData(list);
      setMeta(m);
      return res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load companies';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.status, params.balance, params.industry, params.page, params.limit, params.sort]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const m = meta;
  const pagination = m
    ? { page: m.page ?? 1, totalPages: m.pages ?? 1, total: m.total ?? 0 }
    : { page: 1, totalPages: 1, total: 0 };

  return { data, meta, pagination, loading, error, refetch: fetchList };
}

export function useProfessionalCustomerById(customerCode) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!customerCode) { setData(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${PRO_BASE}/${customerCode}`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load company';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [customerCode]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

export function useCreateProfessionalCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        tradingName: payload.companyName,
        legalName: payload.legalName,
        vatNumber: payload.vatNumber,
        industry: payload.industry,
        country: payload.address?.country ?? payload.country,
        website: payload.website,
        street: payload.address?.street,
        houseNumber: payload.address?.houseNumber || undefined,
        boxNumber: payload.address?.boxNumber || undefined,
        city: payload.address?.city,
        postalCode: payload.address?.zip,
        hasInvoiceEmail: payload.hasInvoiceEmail ?? true,
        invoiceEmail: payload.hasInvoiceEmail === false ? null : payload.invoicing?.email,
        invoicingAddress: payload.invoicing?.address,
        paymentTerms: payload.invoicing?.paymentTerms,
        primaryContactFirstName: payload.primaryContact?.name?.split(' ')[0] ?? '',
        primaryContactLastName: payload.primaryContact?.name?.split(' ').slice(1).join(' ') ?? '',
        hasContactEmail: payload.hasContactEmail ?? true,
        primaryContactEmail: payload.hasContactEmail === false ? null : (payload.primaryContact?.email ?? ''),
        primaryContactPhone: payload.primaryContact?.phone ?? '',
        primaryContactRole: payload.primaryContact?.role,
        preferredLanguage: payload.primaryContact?.language,
      };
      const { data: res } = await axiosInstance.post(PRO_BASE, apiPayload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create company';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

export function useUpdateProfessionalCustomer() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (customerCode, payload) => {
    if (!customerCode) throw new Error('Customer code is required');
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        tradingName: payload.companyName,
        legalName: payload.legalName,
        vatNumber: payload.vatNumber,
        industry: payload.industry,
        street: payload.address?.street,
        houseNumber: payload.address?.houseNumber || undefined,
        boxNumber: payload.address?.boxNumber || undefined,
        city: payload.address?.city,
        postalCode: payload.address?.zip,
        country: payload.address?.country ?? payload.country,
        hasInvoiceEmail: payload.hasInvoiceEmail ?? true,
        invoiceEmail: payload.hasInvoiceEmail === false ? null : payload.invoicing?.email,
        hasContactEmail: payload.hasContactEmail ?? true,
        primaryContactEmail: payload.hasContactEmail === false ? null : (payload.primaryContact?.email ?? undefined),
        primaryContactPhone: payload.primaryContact?.phone ?? undefined,
      };
      const { data: res } = await axiosInstance.put(`${PRO_BASE}/${customerCode}`, apiPayload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update company';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

// ── Travelers / Profiles ─────────────────────────────────────────────────

const TRAVELERS_BASE = '/travellers';
const TRAVELLER_BASE = '/travellers';
const TRAVELLER_RULES_BASE = '/travellers/traveller-rules';

/**
 * useTravelersList - fetch traveler profiles with filters
 * @param {Object} params - { q, status, tab, nationality, page, limit, sort }
 * @returns { data, meta, pagination, loading, error, refetch }
 */
export function useTravelersList(params = {}) {
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const limit = params.limit ?? 10;
      const page = params.page ?? 1;
      const offset = (page - 1) * limit;

      const queryParams = {};
      if (params.q?.trim()) queryParams.q = params.q.trim();
      if (params.status && params.status !== 'All') {
        queryParams.status = params.status.toUpperCase();
      }
      if (params.tab && ['INF', 'CHD', 'ADT'].includes(params.tab.toUpperCase())) {
        queryParams.tab = params.tab.toUpperCase();
      }
      if (params.nationality) queryParams.nationality = params.nationality;
      queryParams.limit = limit;
      queryParams.offset = offset;
      if (params.sort) queryParams.sort = params.sort;

      const { data: res } = await axiosInstance.get(TRAVELERS_BASE, { params: queryParams });
      const list = res?.data ?? [];
      const m = res?.meta ?? null;
      setData(list);
      setMeta(m);

      const pagination = m
        ? { page: m.page ?? page, totalPages: m.pages ?? 1, total: m.total ?? list.length }
        : { page, totalPages: 1, total: list.length };
      return { ...res, pagination };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load travelers';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.status, params.tab, params.nationality, params.page, params.limit, params.sort]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const meta_ = meta;
  const pagination = meta_
    ? { page: meta_.page ?? 1, totalPages: meta_.pages ?? 1, total: meta_.total ?? 0 }
    : { page: 1, totalPages: 1, total: 0 };

  return { data, meta, pagination, loading, error, refetch: fetchList };
}

/**
 * useTravelerById - fetch single traveler by ID (travellerCode)
 */
export function useTravelerById(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${TRAVELERS_BASE}/${id}`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load traveler';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

/**
 * useCreateTraveler - create traveler
 */
export function useCreateTraveler() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        title: payload.title,
        firstName: payload.firstName,
        lastName: payload.lastName,
        gender: ['MALE', 'FEMALE', 'OTHER'].includes(payload.gender?.toUpperCase()) ? payload.gender.toUpperCase() : undefined,
        nationality: payload.nationality,
        dateOfBirth: payload.dateOfBirth,
        ageType: payload.ageType,
      };
      const { data: res } = await axiosInstance.post(TRAVELLER_BASE, apiPayload);
      return { success: true, data: res?.data ?? res };
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message || 'Failed to create traveler';
      const existingCode = err.response?.data?.data?.travellerCode ?? null;
      setError(msg);
      return { success: false, status, message: msg, existingCode };
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useUpdateTraveler - update traveler
 */
export function useUpdateTraveler() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (id, payload) => {
    if (!id) throw new Error('Traveler ID is required');
    setLoading(true);
    setError(null);
    try {
      const apiPayload = {
        title: payload.title,
        firstName: payload.firstName,
        lastName: payload.lastName,
        gender: ['MALE', 'FEMALE', 'OTHER'].includes(payload.gender?.toUpperCase()) ? payload.gender.toUpperCase() : undefined,
        nationality: payload.nationality,
        dateOfBirth: payload.dateOfBirth,
      };
      const { data: res } = await axiosInstance.put(`${TRAVELERS_BASE}/${id}`, apiPayload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update traveler';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useSetTravelerStatus - set traveler status (ACTIVE/INACTIVE)
 */
export function useSetTravelerStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (id, status) => {
    if (!id) throw new Error('Traveler ID is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${TRAVELERS_BASE}/${id}/status`, {
        status: status?.toUpperCase?.() || status,
      });
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update traveler status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useAddTravelerNote - add note to traveler
 */
export function useAddTravelerNote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (travelerId, payload) => {
    if (!travelerId) throw new Error('Traveler ID is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(`${TRAVELERS_BASE}/${travelerId}/notes`, payload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to add note';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useUpdateTravelerNote - update traveler note
 */
export function useUpdateTravelerNote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (travelerId, noteId, payload) => {
    if (!travelerId || !noteId) throw new Error('Traveler ID and Note ID are required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(
        `${TRAVELERS_BASE}/${travelerId}/notes/${noteId}`,
        payload
      );
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update note';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useDeleteTraveler - hard delete traveler by travellerCode
 */
export function useDeleteTraveler() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Traveler ID is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.delete(`${TRAVELERS_BASE}/${id}`);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete traveler';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useDeleteTravelerNote - delete traveler note
 */
export function useDeleteTravelerNote() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (travelerId, noteId) => {
    if (!travelerId || !noteId) throw new Error('Traveler ID and Note ID are required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.delete(
        `${TRAVELERS_BASE}/${travelerId}/notes/${noteId}`
      );
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete note';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useTravelerRules - fetch traveler rules (INF, CHD, ADT definitions)
 */
export function useTravelerRules() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(TRAVELLER_RULES_BASE);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load traveler rules';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return { data, loading, error, refetch: fetchRules };
}

/**
 * useUpdateTravelerRules - update traveler rules
 */
export function useUpdateTravelerRules() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(TRAVELLER_RULES_BASE, payload);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update traveler rules';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

/**
 * useResetTravelerRules - reset traveler rules to defaults
 */
export function useResetTravelerRules() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const clearError = useCallback(() => setError(null), []);

  const execute = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(`${TRAVELLER_RULES_BASE}/reset`);
      return res?.data ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to reset traveler rules';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error, clearError };
}

// ── People Filters (for dropdowns) ─────────────────────────────────────────

/**
 * usePeopleFilters - fetch filter options (balance, industry - for professional)
 */
export function usePeopleFilters() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFilters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(PEOPLE_FILTERS);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load filters';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  return { data, loading, error, refetch: fetchFilters };
}
